import * as React from 'react';
import PropTypes from 'prop-types';
import { toReactNode } from '@godaddy/gasket-content-components';
import { withManifest } from '@godaddy/gasket-hcs';
import { IconRegistry } from '@site/icons/registry/icon-registry';
import dynamicSymbolImports from '@site/icons/dynamic-symbols';
import { componentMap } from './component-map.js';
import { splitPlacement } from '../lib/utils.js';
import { ManifestContext } from '@site/pattern-library-facade/hcs';
import equal from 'fast-deep-equal';

import '@ux/browser-deprecation-banner/styles';
import './styles.scss';

/**
 * @typedef {import('@godaddy/gasket-content-nodes').ContentNode} ContentNode
 */

/**
 * Adds manifestProps to a content node.
 * @param {ContentNode} contentNode - The content node to add manifestProps to
 * @param {*} manifestProps - The manifestProps to add to the content node
 * @returns {ContentNode | null}
 */
export function nodeWithManifestProps(contentNode, manifestProps) {
  if (!contentNode) {
    return null;
  } else if (!manifestProps) {
    return contentNode;
  }
  let [, props] = contentNode;
  // props may be null which is why we use ??= instead of default assignment
  props ??= {};
  props.manifestProps = manifestProps;
  return contentNode;
}

/**
 * React Component to render the Header.
 * This component must be present, but may return null if no header is required.
 *
 * @param {Object} props The props for this component
 * @returns {React.ReactElement} JSX for header
 */
export const Header = (props) => {
  const { header, aboveHeader, ...manifestProps } = props;

  const previousManifestProps = React.useRef(manifestProps);
  if (!equal(manifestProps, previousManifestProps.current)) {
    previousManifestProps.current = manifestProps;
  }
  const stableManifestProps = previousManifestProps.current;

  const reactNode = React.useMemo(() => toReactNode(header, componentMap), [header]);

  return (
    <>
      <React.Suspense fallback={ null }>
        <IconRegistry loader={ (name) => React.lazy(dynamicSymbolImports[name]) } />
      </React.Suspense>
      { aboveHeader ? aboveHeader.map((node) => toReactNode(node, componentMap)) : null }
      <ManifestContext.Provider value={ stableManifestProps }>
        { reactNode }
      </ManifestContext.Provider>
    </>
  );
};

Header.propTypes = {
  header: PropTypes.array.isRequired,
  aboveHeader: PropTypes.array
};


const AUTH_PLACEMENT_DISALLOW_LIST = ['static'];

const doInitCustomerState = ({ split = ''
}) => {
  const splits = split.split(',');
  const headerPlacement = splitPlacement(splits);
  return !AUTH_PLACEMENT_DISALLOW_LIST.some((disallowed) => headerPlacement?.includes(disallowed));
};

/**
 * React Component to render the Header with withManifest HOC.
 *
 * @param {Object} props The props for Header component
 * @returns {React.ReactElement} JSX for exported header
 */
export default function (props) {
  const doAuth = doInitCustomerState(props);
  const HeaderComponent = withManifest(Header, {
    renderAccountDelegation: doAuth,
    initCustomerState: doAuth,
    initTraffic: true
  });

  return (
    <HeaderComponent
      { ...props }
      skipToMainContentLink={{
        optionalAttributes: {
          'data-track-name': 'skip_navigation_link',
          'data-track-eid-click': ''
        }
      }}
    />
  );
}
